/**
 * Guard for navigation triggered by notification action URLs.
 *
 * Notifications carry an `action_url`/`actionUrl` set by the backend. A bug
 * (or a mis-targeted broadcast) can deliver a notification whose URL points at
 * an area the recipient can't access — e.g. an `/admin/...` link delivered to a
 * client. The global NotificationListener then navigates there blindly and the
 * user lands on an "Access Denied" page. We refuse to follow URLs the current
 * role isn't allowed into, so a stray link can never yank a user off their page.
 */

// Path prefixes that are restricted to specific roles.
const ROLE_RESTRICTED_PREFIXES = [
  { prefix: '/admin', roles: ['admin'] },
];

/**
 * @param {string} url   the action URL from a notification
 * @param {string} role  the current user's role
 * @returns {boolean} true if the role may navigate to this URL
 */
export const isPathAllowedForRole = (url, role) => {
  if (!url || typeof url !== 'string') return false;

  let path = url;
  try {
    if (/^https?:\/\//i.test(url)) path = new URL(url).pathname;
  } catch (e) {
    // Not a parseable absolute URL — treat the raw string as the path.
  }

  for (const { prefix, roles } of ROLE_RESTRICTED_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return roles.includes(role);
    }
  }
  return true;
};

export default isPathAllowedForRole;
